/**
 * i18n foundation (the 4A-1 "built i18n-ready" deliverable).
 *
 * Every user-facing string lives in a locale JSON and is reached through t().
 * Phase 8 adds PT-BR / ES / FR by dropping in files and extending LOCALES — no
 * component changes. Dependency-free on purpose: a full i18n library (ICU
 * plurals, lazy loading) is Phase 8's decision, not a foundation cost.
 *
 * `useT()` (i18n/useT.ts) makes translation reactive so a language switcher
 * re-renders the tree; `t()` here is the underlying pure lookup.
 */
import en from './en.json';
import ht from './ht.json';

export type Locale = 'en' | 'ht' | 'pt-BR' | 'es' | 'fr';
type Dict = Record<string, string>;

/**
 * Haitian Creole ships in 4B-2 rather than Phase 8: it is the primary language
 * of the launch audience, so it is a launch requirement rather than an
 * expansion. PT-BR / ES / FR still arrive in Phase 8 the same way — a JSON file
 * and one line here, with no component changes.
 */
const LOCALES: Partial<Record<Locale, Dict>> = { en, ht };

/** Shown in the language picker. Endonyms, so a speaker recognises their own. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ht: 'Kreyòl Ayisyen',
  'pt-BR': 'Português (BR)',
  es: 'Español',
  fr: 'Français',
};
const FALLBACK: Locale = 'en';
const STORAGE_KEY = 'smallx_lang';

let current: Locale = FALLBACK;
const listeners = new Set<() => void>();

/** Pick a locale: saved choice → browser preference → English. */
export function initLocale(): Locale {
  const saved = safeGet(STORAGE_KEY) as Locale | null;
  if (saved && LOCALES[saved]) {
    current = saved;
  } else {
    const nav = typeof navigator !== 'undefined' ? navigator.language : '';
    const exact = Object.keys(LOCALES).find((l) => l.toLowerCase() === nav.toLowerCase());
    const base = Object.keys(LOCALES).find((l) => l.split('-')[0] === nav.split('-')[0]);
    current = ((exact ?? base) as Locale) ?? FALLBACK;
  }
  document.documentElement.lang = current;
  return current;
}

export function setLocale(l: Locale): void {
  if (!LOCALES[l] || l === current) return;
  current = l;
  safeSet(STORAGE_KEY, l);
  document.documentElement.lang = l;
  listeners.forEach((fn) => fn());
}

export function getLocale(): Locale {
  return current;
}

export function availableLocales(): Locale[] {
  return Object.keys(LOCALES) as Locale[];
}

/** Subscribe to locale changes (used by useT to re-render). */
export function subscribeLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Translate. Falls back to English, then to the key itself — a missing string
 * shows the key (obvious in review) rather than a blank, and never throws.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const dict = LOCALES[current] ?? LOCALES[FALLBACK]!;
  const raw = dict[key] ?? LOCALES[FALLBACK]?.[key] ?? key;
  return vars ? interpolate(raw, vars) : raw;
}

function interpolate(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

function safeGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* private mode — non-fatal */
  }
}
